/**
 * Core Layer - Environment Validation Service
 * Validates and parses environment variables with type safety
 */

/**
 * Environment variable schema types
 */
type EnvType = "string" | "number" | "boolean" | "array" | "enum";

interface EnvSchemaBase {
  /** Whether the variable is required */
  required?: boolean;
  /** Default value if not provided */
  default?: unknown;
  /** Description for documentation */
  description?: string;
}

interface StringEnvSchema extends EnvSchemaBase {
  type: "string";
  /** Minimum length for string */
  minLength?: number;
  /** Maximum length for string */
  maxLength?: number;
  /** Regex pattern for validation */
  pattern?: RegExp;
  default?: string;
}

interface NumberEnvSchema extends EnvSchemaBase {
  type: "number";
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  default?: number;
}

interface BooleanEnvSchema extends EnvSchemaBase {
  type: "boolean";
  default?: boolean;
}

interface ArrayEnvSchema extends EnvSchemaBase {
  type: "array";
  /** Separator for array items (default: ",") */
  separator?: string;
  /** Minimum number of items */
  minItems?: number;
  /** Maximum number of items */
  maxItems?: number;
  default?: string[];
}

interface EnumEnvSchema extends EnvSchemaBase {
  type: "enum";
  /** Allowed values */
  values: readonly string[];
  default?: string;
}

type EnvSchema =
  | StringEnvSchema
  | NumberEnvSchema
  | BooleanEnvSchema
  | ArrayEnvSchema
  | EnumEnvSchema;

/**
 * Validation error structure
 */
interface EnvValidationError {
  key: string;
  message: string;
  value?: string;
}

/**
 * Validation result
 */
interface EnvValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: EnvValidationError[];
}

/**
 * Infer TypeScript type from schema
 */
type InferEnvType<S extends EnvSchema> = S extends StringEnvSchema
  ? string
  : S extends NumberEnvSchema
  ? number
  : S extends BooleanEnvSchema
  ? boolean
  : S extends ArrayEnvSchema
  ? string[]
  : S extends EnumEnvSchema
  ? S["values"][number]
  : never;

/**
 * Infer full config type from schema map
 */
type InferEnvConfig<T extends Record<string, EnvSchema>> = {
  [K in keyof T]: T[K]["required"] extends true
    ? InferEnvType<T[K]>
    : T[K]["default"] extends undefined
    ? InferEnvType<T[K]> | undefined
    : InferEnvType<T[K]>;
};

/**
 * Environment Validator Class
 * Validates and parses environment variables based on schema
 */
class EnvValidator<T extends Record<string, EnvSchema>> {
  private schema: T;
  private prefix: string;

  constructor(schema: T, prefix: string = "") {
    this.schema = schema;
    this.prefix = prefix;
  }

  /**
   * Get the full environment variable key
   */
  private getEnvKey(key: string): string {
    return this.prefix ? `${this.prefix}${key}` : key;
  }

  /**
   * Get raw value from environment
   */
  private getRawValue(key: string): string | undefined {
    const envKey = this.getEnvKey(key);
    return process.env[envKey];
  }

  /**
   * Parse string value
   */
  private parseString(
    value: string | undefined,
    schema: StringEnvSchema,
    key: string
  ): { value?: string; error?: EnvValidationError } {
    if (value === undefined || value === "") {
      if (schema.required && schema.default === undefined) {
        return {
          error: { key, message: `Required environment variable is missing` },
        };
      }
      return { value: schema.default };
    }

    if (schema.minLength && value.length < schema.minLength) {
      return {
        error: {
          key,
          message: `Value must be at least ${schema.minLength} characters`,
          value,
        },
      };
    }

    if (schema.maxLength && value.length > schema.maxLength) {
      return {
        error: {
          key,
          message: `Value must be at most ${schema.maxLength} characters`,
          value,
        },
      };
    }

    if (schema.pattern && !schema.pattern.test(value)) {
      return {
        error: {
          key,
          message: `Value does not match required pattern`,
          value,
        },
      };
    }

    return { value };
  }

  /**
   * Parse number value
   */
  private parseNumber(
    value: string | undefined,
    schema: NumberEnvSchema,
    key: string
  ): { value?: number; error?: EnvValidationError } {
    if (value === undefined || value === "") {
      if (schema.required && schema.default === undefined) {
        return {
          error: { key, message: `Required environment variable is missing` },
        };
      }
      return { value: schema.default };
    }

    const num = Number(value);
    if (isNaN(num)) {
      return {
        error: { key, message: `Value must be a valid number`, value },
      };
    }

    if (schema.min !== undefined && num < schema.min) {
      return {
        error: {
          key,
          message: `Value must be at least ${schema.min}`,
          value,
        },
      };
    }

    if (schema.max !== undefined && num > schema.max) {
      return {
        error: {
          key,
          message: `Value must be at most ${schema.max}`,
          value,
        },
      };
    }

    return { value: num };
  }

  /**
   * Parse boolean value
   */
  private parseBoolean(
    value: string | undefined,
    schema: BooleanEnvSchema,
    key: string
  ): { value?: boolean; error?: EnvValidationError } {
    if (value === undefined || value === "") {
      if (schema.required && schema.default === undefined) {
        return {
          error: { key, message: `Required environment variable is missing` },
        };
      }
      return { value: schema.default };
    }

    const lowered = value.toLowerCase();
    if (["true", "1", "yes", "on"].includes(lowered)) {
      return { value: true };
    }
    if (["false", "0", "no", "off"].includes(lowered)) {
      return { value: false };
    }

    return {
      error: {
        key,
        message: `Value must be a boolean (true/false, 1/0, yes/no)`,
        value,
      },
    };
  }

  /**
   * Parse array value
   */
  private parseArray(
    value: string | undefined,
    schema: ArrayEnvSchema,
    key: string
  ): { value?: string[]; error?: EnvValidationError } {
    if (value === undefined || value === "") {
      if (schema.required && schema.default === undefined) {
        return {
          error: { key, message: `Required environment variable is missing` },
        };
      }
      return { value: schema.default ?? [] };
    }

    const separator = schema.separator ?? ",";
    const items = value
      .split(separator)
      .map((item) => item.trim())
      .filter((item) => item !== "");

    if (schema.minItems && items.length < schema.minItems) {
      return {
        error: {
          key,
          message: `Array must have at least ${schema.minItems} items`,
          value,
        },
      };
    }

    if (schema.maxItems && items.length > schema.maxItems) {
      return {
        error: {
          key,
          message: `Array must have at most ${schema.maxItems} items`,
          value,
        },
      };
    }

    return { value: items };
  }

  /**
   * Parse enum value
   */
  private parseEnum(
    value: string | undefined,
    schema: EnumEnvSchema,
    key: string
  ): { value?: string; error?: EnvValidationError } {
    if (value === undefined || value === "") {
      if (schema.required && schema.default === undefined) {
        return {
          error: { key, message: `Required environment variable is missing` },
        };
      }
      return { value: schema.default };
    }

    if (!schema.values.includes(value)) {
      return {
        error: {
          key,
          message: `Value must be one of: ${schema.values.join(", ")}`,
          value,
        },
      };
    }

    return { value };
  }

  /**
   * Parse a single value based on schema
   */
  private parseValue(
    key: string,
    schema: EnvSchema
  ): { value?: unknown; error?: EnvValidationError } {
    const rawValue = this.getRawValue(key);
    const envKey = this.getEnvKey(key);

    switch (schema.type) {
      case "string":
        return this.parseString(rawValue, schema, envKey);
      case "number":
        return this.parseNumber(rawValue, schema, envKey);
      case "boolean":
        return this.parseBoolean(rawValue, schema, envKey);
      case "array":
        return this.parseArray(rawValue, schema, envKey);
      case "enum":
        return this.parseEnum(rawValue, schema, envKey);
      default:
        return { error: { key: envKey, message: `Unknown schema type` } };
    }
  }

  /**
   * Validate all environment variables
   * @throws Error if validation fails in production
   */
  validate(): EnvValidationResult<InferEnvConfig<T>> {
    const errors: EnvValidationError[] = [];
    const data: Record<string, unknown> = {};

    for (const [key, schema] of Object.entries(this.schema)) {
      const result = this.parseValue(key, schema);
      if (result.error) {
        errors.push(result.error);
      } else {
        data[key] = result.value;
      }
    }

    if (errors.length > 0) {
      return { success: false, errors };
    }

    return { success: true, data: data as InferEnvConfig<T> };
  }

  /**
   * Validate and throw if invalid
   * Use this for required validation at app startup
   */
  validateOrThrow(): InferEnvConfig<T> {
    const result = this.validate();

    if (!result.success) {
      const errorMessages = result
        .errors!.map(
          (e) =>
            `  - ${e.key}: ${e.message}${e.value ? ` (got: "${e.value}")` : ""}`
        )
        .join("\n");

      throw new Error(
        `Environment validation failed:\n${errorMessages}\n\nPlease check your .env file.`
      );
    }

    return result.data!;
  }

  /**
   * Get a single validated value
   */
  get<K extends keyof T>(key: K): InferEnvType<T[K]> | undefined {
    const schema = this.schema[key];
    const result = this.parseValue(key as string, schema);
    return result.value as InferEnvType<T[K]> | undefined;
  }
}

/**
 * Create an environment validator
 */
export function createEnvValidator<T extends Record<string, EnvSchema>>(
  schema: T,
  prefix: string = ""
): EnvValidator<T> {
  return new EnvValidator(schema, prefix);
}

/**
 * Schema builder helpers for better DX
 */
export const envSchema = {
  string: (options: Omit<StringEnvSchema, "type"> = {}): StringEnvSchema => ({
    type: "string",
    ...options,
  }),

  number: (options: Omit<NumberEnvSchema, "type"> = {}): NumberEnvSchema => ({
    type: "number",
    ...options,
  }),

  boolean: (
    options: Omit<BooleanEnvSchema, "type"> = {}
  ): BooleanEnvSchema => ({
    type: "boolean",
    ...options,
  }),

  array: (options: Omit<ArrayEnvSchema, "type"> = {}): ArrayEnvSchema => ({
    type: "array",
    ...options,
  }),

  enum: <T extends readonly string[]>(
    values: T,
    options: Omit<EnumEnvSchema, "type" | "values"> = {}
  ): EnumEnvSchema & { values: T } => ({
    type: "enum",
    values,
    ...options,
  }),
};

// Export types for external use
export type {
  EnvSchema,
  EnvValidationError,
  EnvValidationResult,
  InferEnvConfig,
  InferEnvType,
};
